import { useNotification } from 'admin';
import { TrackedEvents, trackEvent } from 'analytics';
import { logger } from 'monitor';
import { useState } from 'react';

export const useToggleCamera = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [hasMotionPermission, setHasMotionPermission] = useState(false);
  const [hasOrientationPermission, setHasOrientationPermission] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const { open } = useNotification();

  const requestMotionPermission = async () => {
    // iOS specific permission request for motion
    // @ts-expect-error - DeviceMotionEvent.requestPermission is not in TypeScript types
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        // @ts-expect-error - DeviceMotionEvent.requestPermission is not in TypeScript types
        const permissionState = await DeviceMotionEvent.requestPermission();
        setHasMotionPermission(permissionState === 'granted');

        if (permissionState === 'denied') {
          logger.warn('iOS Device motion access denied');
          trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
            permission: 'motion',
            platform: 'iOS',
          });
          open?.({
            message: 'Please enable motion access in your device settings',
            type: 'error',
          });
          return false;
        }
        return permissionState === 'granted';
      } catch (error) {
        logger.error('Error requesting motion permission:', error as Error);
        setHasMotionPermission(false);
        return false;
      }
    } else {
      // Non-iOS devices don't need explicit permission
      setHasMotionPermission(true);
      return true;
    }
  };

  const requestOrientationPermission = async () => {
    // iOS specific event that triggers a permission request
    // @ts-expect-error mismatch permission name https://developer.mozilla.org/en-US/docs/Web/API/Permissions
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        // @ts-expect-error mismatch permission name https://developer.mozilla.org/en-US/docs/Web/API/Permissions
        const result = await DeviceOrientationEvent.requestPermission();
        setHasOrientationPermission(result === 'granted');

        if (result === 'denied') {
          logger.warn('iOS Device orientation access denied');
          trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
            permission: 'orientation',
            platform: 'iOS',
          });
          open?.({
            message: 'Please enable orientation access in your device settings',
            type: 'error',
          });
          return false;
        }
        return result === 'granted';
      } catch (error) {
        logger.error('Error requesting orientation permission:', error as Error);
        setHasOrientationPermission(false);
        return false;
      }
    } else {
      // Non-iOS devices don't need explicit permission
      setHasOrientationPermission(true);
      return true;
    }
  };

  const requestCameraPermission = async () => {
    try {
      // @ts-expect-error mismatch permission name https://developer.mozilla.org/en-US/docs/Web/API/Permissions
      const query = await navigator.permissions.query({ name: 'camera' });

      if (query.state === 'denied') {
        logger.warn('Camera access denied');
        trackEvent(TrackedEvents.EVIDENCE_CAMERA_ACCESS_PERMISSIONS_DENIED, {
          permission: 'camera',
        });
        setHasPermission(false);
        open?.({
          message: 'Please enable camera access in your device settings',
          type: 'error',
        });
        return false;
      }

      setHasPermission(true);
      return true;
    } catch (error) {
      logger.error('Error requesting camera permission:', error as Error);
      setHasPermission(false);
      return false;
    }
  };

  const toggle = async () => {
    if (isCameraOpen) {
      setIsCameraOpen(false);
      return;
    }

    const hasOrientation = await requestOrientationPermission();
    const hasMotion = await requestMotionPermission();
    const hasCamera = await requestCameraPermission();

    if (hasOrientation && hasMotion && hasCamera) {
      setIsCameraOpen(true);
    }
  };

  return {
    hasMotionPermission,
    hasOrientationPermission,
    hasPermission,
    isCameraOpen,
    requestCameraPermission,
    requestMotionPermission,
    requestOrientationPermission,
    toggle,
  };
};
